import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { apiFetch, getToken, setToken } from "../utils/authFetch";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unlockedQueue, setUnlockedQueue] = useState([]);

  // Any response that carries achievements_newly_unlocked (login, register,
  // /me, profile update) feeds this queue so <AchievementUnlockPopup /> can
  // show one toast per badge, without every screen having to know about it.
  const enqueueUnlocked = useCallback((maybeUser) => {
    const fresh = maybeUser?.achievements_newly_unlocked;
    if (Array.isArray(fresh) && fresh.length) {
      setUnlockedQueue((prev) => [...prev, ...fresh]);
    }
  }, []);

  const dismissUnlocked = useCallback(() => {
    setUnlockedQueue((prev) => prev.slice(1));
  }, []);

  const refreshUser = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      const data = await apiFetch("/api/auth/me");
      setUser(data.user);
      enqueueUnlocked(data.user);
      return data.user;
    } catch {
      setToken(null);
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [enqueueUnlocked]);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = useCallback(async (email, password, captcha) => {
    const data = await apiFetch("/api/auth/login", {
      method: "POST",
      body: { email, password, captcha_token: captcha?.token, captcha_answer: captcha?.answer },
      auth: false,
    });
    setToken(data.token);
    setUser(data.user);
    enqueueUnlocked(data.user);
    return data.user;
  }, [enqueueUnlocked]);

  const register = useCallback(async (email, password, captcha, securityQuestion, securityAnswer) => {
    const data = await apiFetch("/api/auth/register", {
      method: "POST",
      body: {
        email,
        password,
        captcha_token: captcha?.token,
        captcha_answer: captcha?.answer,
        security_question: securityQuestion,
        security_answer: securityAnswer,
      },
      auth: false,
    });
    setToken(data.token);
    setUser(data.user);
    enqueueUnlocked(data.user);
    return data.user;
  }, [enqueueUnlocked]);

  const logout = useCallback(async () => {
    try {
      await apiFetch("/api/auth/logout", { method: "POST" });
    } catch {
      // ignore any network errors, what matters is clearing local state
    }
    setToken(null);
    setUser(null);
    setUnlockedQueue([]);
  }, []);

  const isStaff = !!user && ["admin", "moderator", "support"].includes(user.role);
  const isAdmin = !!user && user.role === "admin";

  return (
    <AuthContext.Provider
      value={{
        user, setUser, loading, login, register, logout, refreshUser, isStaff, isAdmin,
        unlockedQueue, enqueueUnlocked, dismissUnlocked,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
